import { useNavigate } from "react-router-dom";
import { Button } from "@shop-ledger/ui-components";
import { usePlatformClient } from "../../api/ClientContext";
import { StatusBanner } from "../../components/StatusBanner";
import { useAsyncData } from "../../lib/useAsyncData";

function formatMoney(decimalString: string, currency = "UGX"): string {
  const value = Number(decimalString);
  if (!Number.isFinite(value)) return decimalString;
  return `${currency} ${value.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

/**
 * The real Overview/stats landing page — `GET /platform/stats`
 * (`backend/app/schemas/platform.py::PlatformStatsOut`), a real aggregate query via the
 * BYPASSRLS platform session. This closes the single biggest gap the 2026-07-13
 * business-owner walkthrough found: "platform admin has no usage/statistics dashboard at
 * all — root path just redirects to a raw tenant table." Now the landing route (`/` in
 * `App.tsx`) instead of an immediate redirect to `/tenants`.
 *
 * Genuinely real in both client modes: `mockPlatformApiClient.getPlatformStats` aggregates
 * from its own fixture tenant store rather than returning independent fake numbers, so the
 * totals here are always internally consistent with what `TenantsListPage` shows — no
 * `MockBadge` needed on this page, unlike most of this app's other screens.
 */
export function OverviewPage() {
  const client = usePlatformClient();
  const navigate = useNavigate();
  const { data: stats, loading, error } = useAsyncData(() => client.getPlatformStats(), [client]);

  const activePercent = stats && stats.total_tenants > 0 ? (stats.active_tenants / stats.total_tenants) * 100 : 0;
  const suspendedPercent = stats && stats.total_tenants > 0 ? 100 - activePercent : 0;

  return (
    <div className="page">
      <div className="page__header">
        <div>
          <h1 className="page__title">Platform Overview</h1>
          <p className="page__subtitle">Real aggregate counts across every business on the platform.</p>
        </div>
      </div>

      {error ? <StatusBanner kind="error">{error}</StatusBanner> : null}

      <div className="stat-row">
        <div className="stat-tile">
          <p className="stat-tile__label">Total businesses</p>
          <p className="stat-tile__value">{stats ? stats.total_tenants.toLocaleString() : loading ? "…" : "—"}</p>
        </div>
        <div className="stat-tile">
          <p className="stat-tile__label">Total branches</p>
          <p className="stat-tile__value">{stats ? stats.total_branches.toLocaleString() : loading ? "…" : "—"}</p>
        </div>
        <div className="stat-tile">
          <p className="stat-tile__label">Total users</p>
          <p className="stat-tile__value">{stats ? stats.total_users.toLocaleString() : loading ? "…" : "—"}</p>
        </div>
        <div className="stat-tile">
          <p className="stat-tile__label">Sales (last 30 days)</p>
          <p className="stat-tile__value">
            {stats ? stats.sales_count_last_30d.toLocaleString() : loading ? "…" : "—"}
          </p>
        </div>
        <div className="stat-tile">
          <p className="stat-tile__label">Revenue (last 30 days)</p>
          <p className="stat-tile__value">
            {stats ? formatMoney(stats.sales_revenue_last_30d) : loading ? "…" : "—"}
          </p>
        </div>
      </div>

      <div className="panel">
        <h2 className="panel__title">Business status split</h2>
        {stats ? (
          <>
            <div className="status-split-bar" role="img" aria-label={`${stats.active_tenants} active, ${stats.suspended_tenants} suspended`}>
              {stats.active_tenants > 0 ? (
                <div
                  className="status-split-bar__segment status-split-bar__segment--active"
                  style={{ flexGrow: activePercent }}
                />
              ) : null}
              {stats.suspended_tenants > 0 ? (
                <div
                  className="status-split-bar__segment status-split-bar__segment--suspended"
                  style={{ flexGrow: suspendedPercent }}
                />
              ) : null}
            </div>
            <ul className="status-split-legend">
              <li>
                <span className="status-split-legend__swatch status-split-legend__swatch--active" />
                Active — {stats.active_tenants.toLocaleString()} ({activePercent.toFixed(0)}%)
              </li>
              <li>
                <span className="status-split-legend__swatch status-split-legend__swatch--suspended" />
                Suspended — {stats.suspended_tenants.toLocaleString()} ({suspendedPercent.toFixed(0)}%)
              </li>
            </ul>
          </>
        ) : (
          <p className="muted">{loading ? "Loading…" : "No data."}</p>
        )}
      </div>

      <div className="panel">
        <h2 className="panel__title">Quick links</h2>
        <div className="form-actions">
          <Button type="button" variant="primary" size="md" onClick={() => navigate("/tenants")}>
            View all businesses
          </Button>
          <Button type="button" variant="secondary" size="md" onClick={() => navigate("/onboarding")}>
            Onboard a business
          </Button>
          <Button type="button" variant="ghost" size="md" onClick={() => navigate("/queues")}>
            Operational queues
          </Button>
          <Button type="button" variant="ghost" size="md" onClick={() => navigate("/audit-log")}>
            Audit log
          </Button>
        </div>
      </div>
    </div>
  );
}
